import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import DotElastic from "components/DotElastic";
import ShotContext from "components/Shot/ShotContext";
import VideoPlayer, { VideoPlayerRefAttributes } from "components/VideoPlayer";
import { WINDOW_EVENTS } from "constants/constants";

import { captureSnapshot } from "./video_preview_utils";
import styles from "./VideoPreview.module.scss";

type VideoPreviewParams = object;

const VideoPreview: React.FC<VideoPreviewParams> = (_) => {
  const playerRef = useRef<VideoPlayerRefAttributes>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { selectedShot, setImageData, setSelectedFrame } =
    useContext(ShotContext);

  const videoURL = selectedShot?.video_url ?? "";
  const frameCount = selectedShot?.frame_count ?? 0;
  const firstFrame = selectedShot?.first_frame_number ?? 1;
  const fps = selectedShot?.fps ?? 0;
  const startTime = selectedShot?.start_time ?? 0;

  const handleAnnotate = useCallback(
    (e: Event) => {
      if (selectedShot && playerRef.current) {
        const eVideo = playerRef.current.videoRef.current;
        if (!eVideo) return;

        const frame =
          (e as CustomEvent).detail?.frame ||
          playerRef.current.currentFrame + firstFrame; // absolute frame number

        const handleSeeked = () => {
          setImageData(captureSnapshot(eVideo).toDataURL("image/jpeg", 1.0));
          setSelectedFrame(frame);
          eVideo.removeEventListener("seeked", handleSeeked);
        };
        eVideo.addEventListener("seeked", handleSeeked);

        playerRef.current?.seekToFrame(frame - firstFrame); // relative frame number
      }
    },
    [firstFrame, setImageData, setSelectedFrame, selectedShot],
  );

  useEffect(() => {
    window.addEventListener(WINDOW_EVENTS.ANNOTATE, handleAnnotate);
    return () => {
      window.removeEventListener(WINDOW_EVENTS.ANNOTATE, handleAnnotate);
    };
  }, [handleAnnotate]);

  return (
    <div className={styles.container}>
      <div className={styles.videoContainer}>
        {!isLoading && selectedShot?.width && selectedShot?.height && (
          <div className={styles.resolutionBadge}>
            {selectedShot.width} x {selectedShot.height}
          </div>
        )}
        <VideoPlayer
          ref={playerRef}
          videoURL={videoURL}
          totalFrames={frameCount}
          firstFrame={firstFrame}
          setIsLoading={setIsLoading}
          fps={fps}
          startTime={startTime}
        />
        {isLoading && (
          <div className={styles.loaderContainer}>
            <DotElastic />
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPreview;
